import React, { useEffect, useMemo, useState } from 'react';
import {
  MobileGameBackside,
  MobileGameHolder,
  MobileGameStructure,
  MobileGameWr,
  MobileStructureMiddle,
  SidebarHolder,
} from '@pascal/pascal-core/table-line/table-components';
import { MobileGameMain } from '../game/mobile';
import {
  GameControllers,
  iframeService,
  platformMessageStore,
  RightSideHolder,
  TableFooter,
  useApplicationStore,
  useSoundWatcher,
  useTranslation,
  useUserCountStore,
  useUserInfoStore,
} from '@pascal/pascal-core';
import { GameControllerTypes } from '@pascal/pascal-core/table-line/t_types/enums/GameControllerTypes';
import { BetHistoryWr } from './betHistory';
import { Settings } from '@pascal/pascal-core/table-line';
import ResultWr from './payoutsAndResultsContainer/resultWr';
import { doChipStore } from '../store/doChipStore';
import { observer } from 'mobx-react-lite';
import { TableMobileHeader } from '@pascal/pascal-core/table-line/table-containers/mobileHeader';
import { GAME_CONTROLLER_TYPES } from '@pascal/pascal-core/shared/core/enums/gameControllerTypes';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  applicationStore,
  gameConfigStore,
  lobbyStore,
  popupStore,
  userInfoStore,
} from '@pascal/pascal-core/shared/core/store';
import LimitAndPayouts from './payoutsAndResultsContainer/limitsAndPayouts';
import { HelpPopup } from '../components/helpPopup';
import logoSrc from '../assets/images/roulette-logo.png';
import { allChipsTotal } from '../helpers/chipsTotal';
import { useClearDemoStore } from '../hooks/useClearDemoStore';
import { tableBetHistoryStore } from '@pascal/pascal-core/shared/core/store/tableBetHistoryStore';
import { winningAmountStore } from '../store/winningAmountStore';
import { favoriteBetsStore } from '../store/favoriteBetsStore';
import { autoBetStore } from '../store/autoBetStore';
import useSoundsByName from '../hooks/useSoundsByName';
import { toUpperCase } from '../helpers/toUpperCase';

const gameControllerComponents = {
  [GameControllerTypes.Help]: <HelpPopup />,
  [GameControllerTypes.History]: <BetHistoryWr />,
  [GameControllerTypes.Settings]: <Settings />,
  [GameControllerTypes.Results]: <ResultWr isShowResultTitles={true} resultTitles={['ROUND_ID', 'RESULT', 'CHECK']} />,
  [GAME_CONTROLLER_TYPES.PAYOUTS_AND_LIMITS]: <LimitAndPayouts />,
};

export const MobileGame = observer(({ isReplay }) => {
  const { lastWinAmount } = winningAmountStore;
  const t = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { userId } = userInfoStore;
  const { popupName } = popupStore;
  const [gameControllerType, setGameControllerType] = useState('');
  const { setSelectedHistoryRow, selectedHistoryRow } = tableBetHistoryStore;
  const { usersCount } = useUserCountStore();
  const { isSkipLobby } = gameConfigStore;
  const { getAllBetsModels: allBetsModels } = doChipStore;
  const { isOpenBetHistorySection } = platformMessageStore;
  const { clearDemoStateHandler } = useClearDemoStore();
  const { isDemo } = useApplicationStore();
  const { currencyCode } = useUserInfoStore();
  const { roomName } = lobbyStore;

  useEffect(() => {
    return () => {
      applicationStore.setIsDemo(false);
      favoriteBetsStore.setFavoriteBes([]);
      doChipStore.resetReBet();
      doChipStore.resetAllBetsModels(true);
      winningAmountStore.setWinningAmount(null);
      winningAmountStore.setWinNumber(null);
      autoBetStore.resetAutoBet();
      winningAmountStore.setLastWin(0);
    };
  }, []);

  useEffect(() => {
    if (popupName && platformMessageStore.isOpenOddsSection) {
      setGameControllerType(GAME_CONTROLLER_TYPES.PAYOUTS_AND_LIMITS);
    }
  }, [popupName, setGameControllerType]);

  const GameControllerComponent = useMemo(() => {
    const component = gameControllerComponents[gameControllerType];
    if (component) {
      return component;
    }
    return null;
  }, [gameControllerType]);

  const navigationItems = useMemo(() => {
    const items = [
      {
        icon: 'icon-home1',
        name: t('HOME'),
        onClick: () => iframeService.exitGame(),
      },
      {
        icon: 'icon-lobby',
        name: t('LOBBY'),
        onClick: () => navigate(`${'lobby'}${search}`),
      },
      {
        icon: 'icon-limits',
        name: t('LIMITS_AND_PAYOUTS'),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.PAYOUTS_AND_LIMITS),
        isPayTable: true,
      },
      {
        icon: 'icon-history',
        name: t('HISTORY'),
        isDisabled: !userId || isDemo,
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY),
      },
      {
        icon: 'icon-settings',
        name: t('SETTINGS'),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.SETTINGS),
      },
      {
        icon: 'icon-help',
        name: t('RULES'),
        onClick: () => setGameControllerType(GAME_CONTROLLER_TYPES.HELP),
      },
    ];
    if (isSkipLobby) {
      items.splice(1, 1);
      return items;
    }

    return items;
  }, [navigate, search, t, userId, isSkipLobby, isDemo]);

  useEffect(() => {
    if (!isReplay) {
      const el = document.createElement('span');
      const parent = document.querySelector('.m-user-count-holder');
      el.classList.add('m-user-count-txt');
      el.innerText = usersCount || '';
      parent.prepend(el);
      if (!usersCount) {
        parent.innerHTML = '';
      }
    }
  }, [usersCount]);

  useEffect(() => {
    if (!gameControllerType) {
      setGameControllerType(false);
    }
    if (isOpenBetHistorySection) {
      setGameControllerType(GAME_CONTROLLER_TYPES.HISTORY);
    }
  }, [isOpenBetHistorySection, gameControllerType]);

  const soundsByName = useSoundsByName();
  useSoundWatcher(toUpperCase(roomName), soundsByName);

  return (
    <MobileGameWr>
      <MobileGameBackside />
      <MobileGameStructure hasLobby={isSkipLobby} lobbyLogoImageSrc={logoSrc} hasGameTypeLoading>
        {!isReplay && <TableMobileHeader logoSrc={logoSrc} demoSwitcherAdditionalFunction={clearDemoStateHandler} />}
        <MobileStructureMiddle haveNotification translationValues={{}}>
          <MobileGameHolder>
            <MobileGameMain isReplay={isReplay} />
          </MobileGameHolder>
          {!isReplay && (
            <>
              <SidebarHolder setGameControllerType={setGameControllerType} hasSideIcon={true} sideIcon={'icon-chip'}>
                <GameControllers
                  t={t}
                  currencyCode={currencyCode}
                  controllerType={gameControllerType}
                  setGameControllerType={() => setGameControllerType(null)}
                  selectedHistoryRow={selectedHistoryRow}
                  setSelectedHistoryRow={setSelectedHistoryRow}
                  gameControllerComponent={GameControllerComponent}
                />
              </SidebarHolder>
              <RightSideHolder setGameControllerType={setGameControllerType} navigationItems={navigationItems} />
            </>
          )}
        </MobileStructureMiddle>
        {!isReplay && (
          <TableFooter
            lastWin={lastWinAmount}
            totalBet={allChipsTotal(allBetsModels[0]) || 0}
            centerBlock={
              <>
                <div className='user-icon-b'>
                  <i className='icon-users1' />
                </div>
                <div className='m-user-count-holder' />
              </>
            }
          />
        )}
      </MobileGameStructure>
    </MobileGameWr>
  );
});
